// client/src/components/smart-table/utils/clientSideProcessing.js

import handleSummaries from './handleSummaries';

// Implementation of filter functions for client-side filtering
const filterFunctions = {
    text: (rows, operator, colId, comparison) => {
        return rows.filter(row => {
            const value = String(row[colId] || "").toLowerCase();
            const comp = String(comparison || "").toLowerCase();

            switch (operator) {
                case "contains":
                    return value.includes(comp);
                case "does not contain":
                    return !value.includes(comp);
                case "is exactly":
                    return value === comp;
                case "is not exactly":
                    return value !== comp;
                case "is empty":
                    return !value || value.trim() === "";
                case "is not empty":
                    return value && value.trim() !== "";
                default:
                    return true;
            }
        });
    },
    number: (rows, operator, colId, comparison) => {
        return rows.filter(row => {
            const value = parseFloat(row[colId]);
            const comp = parseFloat(comparison);

            if (operator === "is empty") return !row[colId] && row[colId] !== 0;
            if (operator === "is not empty") return row[colId] !== undefined && row[colId] !== null;
            if (isNaN(value)) return false;
            if (operator !== "is empty" && operator !== "is not empty" && isNaN(comp)) return true;


            switch (operator) {
                case "=":
                    return value === comp;
                case "!=":
                    return value !== comp;
                case ">":
                    return value > comp;
                case ">=":
                    return value >= comp;
                case "<":
                    return value < comp;
                case "<=":
                    return value <= comp;
                default:
                    return true;
            }
        });
    },
    date: (rows, operator, colId, comparison) => {
        // Helper function to parse comparison value and return a Date object
        const parseComparisonDate = (comparisonValue) => {
            if (!comparisonValue) return null;

            // Handle the new format: "type:value" or just "type" for predefined options
            let comparisonType, comparisonData;
            if (typeof comparisonValue === 'string' && comparisonValue.includes(':')) {
                [comparisonType, comparisonData] = comparisonValue.split(':');
            } else if (typeof comparisonValue === 'string') {
                // Check if it looks like an ISO date string (exact date stored without prefix)
                if (comparisonValue.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
                    const exactDate = new Date(comparisonValue);
                    // Normalize to midnight UTC for consistent date-only comparison
                    return new Date(Date.UTC(exactDate.getUTCFullYear(), exactDate.getUTCMonth(), exactDate.getUTCDate()));
                }
                comparisonType = comparisonValue;
                comparisonData = '';
            } else {
                comparisonType = comparisonValue;
                comparisonData = '';
            }

            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

            switch (comparisonType) {
                case 'today':
                    return today;
                case 'tomorrow':
                    return new Date(today.getTime() + 24 * 60 * 60 * 1000);
                case 'yesterday':
                    return new Date(today.getTime() - 24 * 60 * 60 * 1000);
                case 'one_week_ago':
                    return new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
                case 'one_week_from_now':
                    return new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
                case 'one_month_ago':
                    const oneMonthAgo = new Date(today);
                    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
                    return oneMonthAgo;
                case 'one_month_from_now':
                    const oneMonthFromNow = new Date(today);
                    oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);
                    return oneMonthFromNow;
                case 'number_of_days_ago':
                    const daysAgo = parseInt(comparisonData, 10);
                    if (isNaN(daysAgo)) return null;
                    return new Date(today.getTime() - daysAgo * 24 * 60 * 60 * 1000);
                case 'number_of_days_from_now':
                    const daysFromNow = parseInt(comparisonData, 10);
                    if (isNaN(daysFromNow)) return null;
                    return new Date(today.getTime() + daysFromNow * 24 * 60 * 60 * 1000);
                case 'exact_date':
                    const exactDate = comparisonData ? new Date(comparisonData) : new Date(comparisonValue);
                    // Normalize to midnight UTC for consistent date-only comparison
                    return new Date(Date.UTC(exactDate.getUTCFullYear(), exactDate.getUTCMonth(), exactDate.getUTCDate()));
                default:
                    // Legacy support for direct date strings
                    return new Date(comparisonValue);
            }
        };

        return rows.filter(row => {
            const value = row[colId] ? new Date(row[colId]) : null;

            if (operator === "is empty") return !value;
            if (operator === "is not empty") return !!value;
            if (!value) return false;

            const comp = parseComparisonDate(comparison);
            if (!comp && operator !== "is empty" && operator !== "is not empty") return true;

            switch (operator) {
                case "is":
                    const valueNormalized = new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()));
                    const compNormalized = new Date(Date.UTC(comp.getUTCFullYear(), comp.getUTCMonth(), comp.getUTCDate()));
                    return valueNormalized.getTime() === compNormalized.getTime();
                case "is not":
                    const valueNotNormalized = new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()));
                    const compNotNormalized = new Date(Date.UTC(comp.getUTCFullYear(), comp.getUTCMonth(), comp.getUTCDate()));
                    return valueNotNormalized.getTime() !== compNotNormalized.getTime();
                case "is before":
                case "before": // Legacy support
                    const valueBefore = new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()));
                    const compBefore = new Date(Date.UTC(comp.getUTCFullYear(), comp.getUTCMonth(), comp.getUTCDate()));
                    return valueBefore < compBefore;
                case "is after":
                case "after": // Legacy support
                    const valueAfter = new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()));
                    const compAfter = new Date(Date.UTC(comp.getUTCFullYear(), comp.getUTCMonth(), comp.getUTCDate()));
                    return valueAfter > compAfter;
                case "is on or before":
                    const valueOnOrBefore = new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()));
                    const compOnOrBefore = new Date(Date.UTC(comp.getUTCFullYear(), comp.getUTCMonth(), comp.getUTCDate()));
                    return valueOnOrBefore <= compOnOrBefore;
                case "is on or after":
                    const valueOnOrAfter = new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()));
                    const compOnOrAfter = new Date(Date.UTC(comp.getUTCFullYear(), comp.getUTCMonth(), comp.getUTCDate()));
                    return valueOnOrAfter >= compOnOrAfter;
                default:
                    return true;
            }
        });
    },
    checkbox: (rows, operator, colId) => {
        return rows.filter(row => {
            const value = !!row[colId];

            switch (operator) {
                case "checked":
                    return value === true;
                case "not checked":
                    return value === false;
                default:
                    return true;
            }
        });
    },
    single_select: (rows, operator, colId, comparison) => {
        return rows.filter(row => {
            const value = row[colId];

            if (operator === "is empty") return !value && value !== 0 && value !== false;
            if (operator === "is not empty") return !!value || value === 0 || value === false;

            switch (operator) {
                case "is":
                    return value === comparison;
                case "is not":
                    return value !== comparison;
                default:
                    return true;
            }
        });
    },
    multi_select: (rows, operator, colId, comparison) => {
        return rows.filter(row => {
            const values = Array.isArray(row[colId]) ? row[colId] : [row[colId]].filter(v => v !== null && v !== undefined);

            if (operator === "is empty") return !values.length;
            if (operator === "is not empty") return values.length > 0;

            switch (operator) {
                case "includes":
                    return values.includes(comparison);
                case "does not include":
                    return !values.includes(comparison);
                default:
                    return true;
            }
        });
    },
    advisor: (rows, operator, colId, comparison) => {
        return rows.filter(row => {
            const advisorObj = row[colId];
            const advisorId = advisorObj?.user_id;
            console.log("advisorId", advisorId);
            console.log("comparison", comparison);
            if (operator === "is empty") return !advisorObj;
            if (operator === "is not empty") return !!advisorObj;

            switch (operator) {
                case "is":
                    return advisorId == comparison;
                case "is not":
                    return advisorId != comparison;
                default:
                    return true;
            }
        });
    }
};

// Implementation of sort functions for client-side sorting
const sortFunctions = {
    text: (rows, direction, colId) => {
        return [...rows].sort((a, b) => {
            const aVal = String(a[colId] || "").toLowerCase();
            const bVal = String(b[colId] || "").toLowerCase();

            if (direction === "asc") {
                return aVal.localeCompare(bVal);
            } else {
                return bVal.localeCompare(aVal);
            }
        });
    },
    number: (rows, direction, colId) => {
        return [...rows].sort((a, b) => {
            const aVal = parseFloat(a[colId]);
            const bVal = parseFloat(b[colId]);

            if (isNaN(aVal) && isNaN(bVal)) return 0;
            if (isNaN(aVal)) return 1;
            if (isNaN(bVal)) return -1;


            if (direction === "asc") {
                return aVal - bVal;
            } else {
                return bVal - aVal;
            }
        });
    },
    date: (rows, direction, colId) => {
        return [...rows].sort((a, b) => {
            const aVal = a[colId] ? new Date(a[colId]) : null;
            const bVal = b[colId] ? new Date(b[colId]) : null;

            if (!aVal && !bVal) return 0;
            if (!aVal) return 1;
            if (!bVal) return -1;

            if (direction === "asc") {
                return aVal - bVal;
            } else {
                return bVal - aVal;
            }
        });
    },
    checkbox: (rows, direction, colId) => {
        return [...rows].sort((a, b) => {
            const aVal = !!a[colId];
            const bVal = !!b[colId];
            if (aVal === bVal) return 0;
            return direction === "asc" ? (aVal ? 1 : -1) : (aVal ? -1 : 1);
        });
    },
    single_select: (rows, direction, colId) => {
        return [...rows].sort((a, b) => {
            const aVal = String(a[colId] || "").toLowerCase();
            const bVal = String(b[colId] || "").toLowerCase();

            if (direction === "asc") {
                return aVal.localeCompare(bVal);
            } else {
                return bVal.localeCompare(aVal);
            }
        });
    },
    multi_select: (rows, direction, colId) => {
        return [...rows].sort((a, b) => {
            const aVal = Array.isArray(a[colId]) ? a[colId].join(",").toLowerCase() : String(a[colId] || "").toLowerCase();
            const bVal = Array.isArray(b[colId]) ? b[colId].join(",").toLowerCase() : String(b[colId] || "").toLowerCase();

            if (direction === "asc") {
                return aVal.localeCompare(bVal);
            } else {
                return bVal.localeCompare(aVal);
            }
        });
    }
};

// Function to apply search across all visible columns
const applySearch = (allRows, searchQuery, visibleColumns) => {
    if (!searchQuery || !searchQuery.trim()) {
        return allRows;
    }

    const query = searchQuery.toLowerCase().trim();

    return allRows.filter(row => {
        // Search across all visible columns
        return visibleColumns.some(column => {
            const value = row[column.col_id];
            if (value === null || value === undefined) return false;

            // Convert value to string for searching
            let searchText = "";

            switch (column.col_type) {
                case "text":
                case "household_link":
                case "link":
                case "single_select":
                    searchText = String(value).toLowerCase();
                    break;
                case "multi_select":
                    if (Array.isArray(value)) {
                        searchText = value.join(" ").toLowerCase();
                    } else {
                        searchText = String(value).toLowerCase();
                    }
                    break;
                case "number":
                    searchText = String(value);
                    break;
                case "date":
                case "due_date":
                    // Format date for search
                    try {
                        const date = new Date(value);
                        searchText = date.toLocaleDateString().toLowerCase();
                    } catch (e) {
                        searchText = String(value).toLowerCase();
                    }
                    break;
                case "checkbox":
                    searchText = value ? "yes true checked" : "no false unchecked";
                    break;
                case "advisor":
                    if (value && typeof value === "object") {
                        searchText = `${value.first_name || ""} ${value.last_name || ""} ${value.email || ""}`.toLowerCase();
                    } else {
                        searchText = String(value).toLowerCase();
                    }
                    break;
                default:
                    searchText = String(value).toLowerCase();
            }

            return searchText.includes(query);
        });
    });
};

// Advanced filter processing function for client-side filtering
const processAdvancedFilters = (rows, filterData, columns) => {
    if (!filterData || !rows || rows.length === 0) {
        return rows;
    }

    // Handle array format (simple conditions with implicit AND)
    if (Array.isArray(filterData)) {
        if (filterData.length === 0) return rows;

        return filterData.reduce((filteredRows, condition) => {
            // Skip if it's a nested group
            if (condition.type === 'group') {
                return processAdvancedFilters(filteredRows, condition, columns);
            }

            const column = columns.find(col => col.col_id === condition.col_id);
            if (!column) return filteredRows;

            const columnType = column.col_type;
            let filterType = "text"; // Default filter type

            // Map column types to filter function types
            if (columnType === "number") filterType = "number";
            else if (columnType === "date" || columnType === "due_date") filterType = "date";
            else if (columnType === "checkbox") filterType = "checkbox";
            else if (columnType === "single_select") filterType = "single_select";
            else if (columnType === "multi_select") filterType = "multi_select";
            else if (columnType === "advisor") filterType = "advisor";

            // Apply the appropriate filter function
            const filterFunction = filterFunctions[filterType];
            if (filterFunction) {
                return filterFunction(filteredRows, condition.operator, condition.col_id, condition.comparison);
            }
            return filteredRows;
        }, rows);
    }

    // Handle single condition
    if (filterData.type === 'condition') {
        const column = columns.find(col => col.col_id === filterData.col_id);
        if (!column) return rows;

        const columnType = column.col_type;
        let filterType = "text"; // Default filter type

        // Map column types to filter function types
        if (columnType === "number") filterType = "number";
        else if (columnType === "date" || columnType === "due_date") filterType = "date";
        else if (columnType === "checkbox") filterType = "checkbox";
        else if (columnType === "single_select") filterType = "single_select";
        else if (columnType === "multi_select") filterType = "multi_select";
        else if (columnType === "advisor") filterType = "advisor";

        const filterFunction = filterFunctions[filterType];
        if (filterFunction) {
            return filterFunction(rows, filterData.operator, filterData.col_id, filterData.comparison);
        }
        return rows;
    }

    // Handle filter groups
    if (filterData.type === 'group' && filterData.conditions) {
        const operator = filterData.operator || 'AND';

        // If no conditions in the group, return all rows
        if (!filterData.conditions || filterData.conditions.length === 0) {
            return rows;
        }

        if (operator === 'AND') {
            // For AND: apply filters sequentially (intersection)
            return filterData.conditions.reduce((filteredRows, condition) => {
                return processAdvancedFilters(filteredRows, condition, columns);
            }, rows);
        } else if (operator === 'OR') {
            // For OR: apply filters separately and combine results (union)
            const allResults = filterData.conditions.map(condition => {
                return processAdvancedFilters(rows, condition, columns);
            });

            // Combine all results and remove duplicates
            const combinedResults = [];
            const seenIds = new Set();

            allResults.forEach(results => {
                results.forEach(row => {
                    // Use a combination of properties to create a unique identifier
                    const rowId = JSON.stringify(row);
                    if (!seenIds.has(rowId)) {
                        seenIds.add(rowId);
                        combinedResults.push(row);
                    }
                });
            });

            return combinedResults;
        }
    }

    return rows;
};

// Main function to apply filters and sorts
const applyFiltersAndSorts = (allRows, columns, sorts = [], filters = []) => {
    if (!allRows || !columns) return allRows || [];

    let filteredRows = [...allRows];

    // Apply advanced filters
    if (filters) {
        // Handle both legacy array format and new group format
        if (Array.isArray(filters) && filters.length === 0) {
            // No filtering needed for empty array
        } else if (filters.type || Array.isArray(filters)) {
            // Process either group format or legacy array format
            filteredRows = processAdvancedFilters(filteredRows, filters, columns);
        }
    }

    // Apply sorts
    if (sorts && sorts.length > 0) {
        // Apply sorts in reverse order (most significant sort applied last)
        [...sorts].reverse().forEach(sort => {
            const column = columns.find(col => col.col_id === sort.col_id);
            if (!column) return;

            const columnType = column.col_type;
            let sortType = "text"; // Default sort type

            // Map column types to sort function types
            if (columnType === "number") sortType = "number";
            else if (columnType === "date" || columnType === "due_date") sortType = "date";
            else if (columnType === "checkbox") sortType = "checkbox";
            else if (columnType === "single_select") sortType = "single_select";
            else if (columnType === "multi_select") sortType = "multi_select";

            // Apply the appropriate sort function
            const sortFunction = sortFunctions[sortType];
            if (sortFunction) {
                filteredRows = sortFunction(filteredRows, sort.direction, sort.col_id);
            }
        });
    }

    return filteredRows;
};

// Function to determine visible columns
const getVisibleColumns = (allColumnsDefinition, hiddenColumnIds = [], columnOrder = []) => {
    if (!allColumnsDefinition) return [];

    // Filter out hidden columns first
    const visibleColumns = allColumnsDefinition.filter(col => !hiddenColumnIds.includes(col.col_id));

    // If no custom order, return visible columns in their original order
    if (!columnOrder || columnOrder.length === 0) {
        return visibleColumns;
    }

    // Create a map for quick lookup
    const columnMap = new Map(visibleColumns.map(col => [col.col_id, col]));
    const orderedColumns = [];

    // Add columns in the specified order (only if they're visible)
    columnOrder.forEach(colId => {
        const column = columnMap.get(colId);
        if (column) {
            orderedColumns.push(column);
            columnMap.delete(colId);
        }
    });

    // Add any remaining visible columns that weren't in the order array
    orderedColumns.push(...Array.from(columnMap.values()));

    return orderedColumns;
};

// Function to apply grouping to rows
const applyGrouping = (rows, grouping = [], visibleColumns = []) => {
    if (!rows || !grouping || grouping.length === 0) {
        return rows;
    }

    // Create a nested grouping structure
    const createGroupStructure = (rows, groupingLevels, level = 0) => {
        if (level >= groupingLevels.length) {
            return rows;
        }

        const currentGrouping = groupingLevels[level];
        const groups = new Map();

        // Group rows by current grouping column
        rows.forEach(row => {
            let groupKey = row[currentGrouping.col_id];

            // Handle null/undefined values
            if (groupKey === null || groupKey === undefined || groupKey === '') {
                groupKey = '(Empty)';
            }

            // For objects (like advisor column), use a string representation
            if (typeof groupKey === 'object' && groupKey !== null) {
                if (groupKey.name) {
                    groupKey = groupKey.name;
                } else if (groupKey.user_id) {
                    groupKey = groupKey.user_id;
                } else {
                    groupKey = JSON.stringify(groupKey);
                }
            }

            if (!groups.has(groupKey)) {
                groups.set(groupKey, []);
            }
            groups.get(groupKey).push(row);
        });

        // Convert groups to array and apply further grouping if needed
        const result = [];
        groups.forEach((groupRows, groupKey) => {
            // Calculate summaries for this group if show_summary is enabled
            let groupSummaries = {};
            if (currentGrouping.show_summary && visibleColumns.length > 0) {
                // Filter out any nested group headers from the group rows before calculating summaries
                const dataRowsOnly = groupRows.filter(row => !row.__isGroupHeader);
                groupSummaries = handleSummaries(dataRowsOnly, visibleColumns);
            }

            const groupHeader = {
                __isGroupHeader: true,
                __groupLevel: level,
                __groupKey: groupKey,
                __groupColId: currentGrouping.col_id,
                __groupRowCount: groupRows.filter(row => !row.__isGroupHeader).length, // Count only data rows
                __showSummary: currentGrouping.show_summary,
                __summariesOnly: currentGrouping.summaries_only,
                __groupSummaries: groupSummaries,
                // Add group key to the grouping column for display
                [currentGrouping.col_id]: `${groupKey} (${groupRows.filter(row => !row.__isGroupHeader).length})`
            };

            result.push(groupHeader);

            // Only include subgroups/data rows if summaries_only is not enabled
            if (!currentGrouping.summaries_only) {
                // Recursively group the remaining levels
                const subGroups = createGroupStructure(groupRows, groupingLevels, level + 1);
                result.push(...subGroups);
            }
        });

        return result;
    };

    return createGroupStructure(rows, grouping);
};

// Function to calculate summaries using the comprehensive frontend summaries logic
const calculateSummaries = (processedRows, visibleColumns) => {
    if (!processedRows || !visibleColumns) return {};

    // Filter out group headers for summary calculations
    const dataRows = processedRows.filter(row => !row.__isGroupHeader);

    // Use the comprehensive handleSummaries function from handleSummaries.js
    const summaries = handleSummaries(dataRows, visibleColumns);

    return summaries;
};


export const applyClientSideProcessing = (allRows, allColumnsDefinition, filters, sorts, hiddenColumnIds, columnOrder, searchQuery = "", grouping = []) => {
    if (!allRows || !allColumnsDefinition) {
        return {
            rows: [],
            visible_columns: [],
            summaries: {},
            filtered_rows_length: 0,
            // total_rows_length is not directly available here, it comes from initial fetch or allRows.length before filtering
        };
    }

    // First determine visible columns (needed for search)
    const visibleCols = getVisibleColumns(allColumnsDefinition, hiddenColumnIds, columnOrder);

    // Apply search first (across all visible columns)
    let processedRows = applySearch(allRows, searchQuery, visibleCols);

    // Then apply filters and sorts to the search results
    processedRows = applyFiltersAndSorts(processedRows, allColumnsDefinition, sorts, filters);

    // Apply grouping after filtering and sorting
    processedRows = applyGrouping(processedRows, grouping, visibleCols);

    // Calculate summaries on final processed rows (excluding group headers)
    const sums = calculateSummaries(processedRows, visibleCols);

    return {
        rows: processedRows,
        visible_columns: visibleCols,
        summaries: sums,
        filtered_rows_length: processedRows.filter(row => !row.__isGroupHeader).length,
        // total_rows_length will be set in SmartTable.jsx based on allRows.length from the fetch
    };
};

export { applyFiltersAndSorts, processAdvancedFilters }; 